// Voice recording defaults on for both sides. Enterprise workspace settings can
// disable both together, while the role-level constants keep the call lifecycle
// and cleanup paths simple.
export const VOICE_RECORD_USER_AUDIO = true;
export const VOICE_RECORD_ASSISTANT_AUDIO = true;
export const VOICE_RECORDING_RETENTION = 'until_deleted';
export const VOICE_RECORDING_MIME_PREFERENCE = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
] as const;
export const VOICE_RECORDING_MIN_CLIP_MS = 300;
export const VOICE_RECORDING_MAX_CLIP_MS = 120_000;
// MediaRecorder's final dataavailable event can land just after the stop timer.
// Keep that bounded scheduler drift while clamping persisted duration metadata
// to MAX_CLIP_MS, and reject genuinely overlong or unexpectedly large blobs.
export const VOICE_RECORDING_MAX_CLIP_DRIFT_TOLERANCE_MS = 1_500;
export const VOICE_RECORDING_MAX_CLIP_BYTES = 12 * 1024 * 1024;

export const VOICE_RECORDING_ENABLED =
  VOICE_RECORD_USER_AUDIO || VOICE_RECORD_ASSISTANT_AUDIO;

export type VoiceRecordingRetentionPolicy = typeof VOICE_RECORDING_RETENTION;
export type VoiceRecordingRole = 'user' | 'assistant';

export type RealtimeVoiceMediaRecorderSupport = {
  isTypeSupported?: (mimeType: string) => boolean;
};

export {
  VOICE_RECORDING_CONTAINER_PROBE_BYTES,
  validateRealtimeVoiceRecordingContainerSignature,
} from './realtime-voice-recording-container';

export type RealtimeVoiceRecordingHeadValidation =
  | { valid: true; byteSize: number; mimeType: string; durationMs: number }
  | {
      valid: false;
      reason: 'invalid_size' | 'invalid_content_type' | 'invalid_duration';
    };

export function isRealtimeVoiceRecordingEnabled(args: {
  recordUserAudio: boolean;
  recordAssistantAudio: boolean;
}): boolean {
  return args.recordUserAudio || args.recordAssistantAudio;
}

export function isRealtimeVoiceRecordingRoleEnabled(args: {
  role: VoiceRecordingRole;
  recordUserAudio: boolean;
  recordAssistantAudio: boolean;
}): boolean {
  return args.role === 'user'
    ? args.recordUserAudio
    : args.recordAssistantAudio;
}

export function getRealtimeVoiceRecordingNotice(args: {
  recordUserAudio: boolean;
  recordAssistantAudio: boolean;
}): string | null {
  if (
    !isRealtimeVoiceRecordingEnabled({
      recordUserAudio: args.recordUserAudio,
      recordAssistantAudio: args.recordAssistantAudio,
    })
  ) {
    return null;
  }
  if (args.recordUserAudio && args.recordAssistantAudio) {
    return 'Voice audio is saved for this session.';
  }
  if (args.recordUserAudio) {
    return 'Your voice audio is saved for this session.';
  }
  return 'Assistant voice audio is saved for this session.';
}

export function selectRealtimeVoiceRecordingMimeType(
  preferences: readonly string[] = VOICE_RECORDING_MIME_PREFERENCE,
  mediaRecorderSupport:
    | RealtimeVoiceMediaRecorderSupport
    | undefined = typeof MediaRecorder === 'undefined'
    ? undefined
    : MediaRecorder
): string | undefined {
  if (!mediaRecorderSupport?.isTypeSupported) return undefined;
  return preferences.find((mimeType) =>
    mediaRecorderSupport.isTypeSupported?.(mimeType)
  );
}

const WEBM_AUDIO_CODECS = new Set(['opus']);
const MP4_AUDIO_CODECS = new Set([
  'aac',
  'mp4a.40.2',
  'mp4a.40.5',
  'mp4a.40.29',
]);

function normalizeCodecParameter(value: string): string | null {
  let normalized = value.trim().toLowerCase();
  const startsQuoted = normalized.startsWith('"');
  const endsQuoted = normalized.endsWith('"');
  if (startsQuoted !== endsQuoted) return null;
  if (startsQuoted) normalized = normalized.slice(1, -1).trim();
  if (!normalized || /["\u0000-\u001f\u007f]/.test(normalized)) return null;
  return normalized;
}

function normalizeRealtimeVoiceRecordingContentType(
  contentType: string
): string | null {
  if (contentType.length > 256) return null;
  const parts = contentType.split(';');
  const mediaType = parts.shift()?.trim().toLowerCase();
  const canonicalMediaType =
    mediaType === 'audio/webm'
      ? 'audio/webm'
      : mediaType === 'audio/mp4' ||
          mediaType === 'audio/x-m4a' ||
          mediaType === 'audio/m4a'
        ? 'audio/mp4'
        : null;
  if (!canonicalMediaType) return null;

  let codec: string | null = null;
  for (const rawParameter of parts) {
    const separatorIndex = rawParameter.indexOf('=');
    if (separatorIndex <= 0) return null;
    const name = rawParameter.slice(0, separatorIndex).trim().toLowerCase();
    if (name !== 'codecs' || codec !== null) return null;
    codec = normalizeCodecParameter(rawParameter.slice(separatorIndex + 1));
    if (!codec || codec.includes(',')) return null;
  }

  const allowedCodecs =
    canonicalMediaType === 'audio/webm' ? WEBM_AUDIO_CODECS : MP4_AUDIO_CODECS;
  if (codec !== null && !allowedCodecs.has(codec)) return null;
  return codec ? `${canonicalMediaType};codecs=${codec}` : canonicalMediaType;
}

export function validateRealtimeVoiceRecordingHeadMetadata(args: {
  contentLength: number | undefined;
  contentType: string | undefined;
  durationMs: number;
}): RealtimeVoiceRecordingHeadValidation {
  const byteSize = args.contentLength;
  if (
    byteSize === undefined ||
    !Number.isSafeInteger(byteSize) ||
    byteSize <= 0 ||
    byteSize > VOICE_RECORDING_MAX_CLIP_BYTES
  ) {
    return { valid: false, reason: 'invalid_size' };
  }
  if (!args.contentType?.trim()) {
    return { valid: false, reason: 'invalid_content_type' };
  }
  const mimeType = normalizeRealtimeVoiceRecordingContentType(args.contentType);
  if (!mimeType) {
    return { valid: false, reason: 'invalid_content_type' };
  }
  if (
    !shouldKeepRealtimeVoiceRecordingClip({
      durationMs: args.durationMs,
      byteSize,
    })
  ) {
    return { valid: false, reason: 'invalid_duration' };
  }
  return {
    valid: true,
    byteSize,
    mimeType,
    durationMs: clampRealtimeVoiceRecordingDuration(args.durationMs),
  };
}

export function shouldKeepRealtimeVoiceRecordingClip(args: {
  durationMs: number;
  byteSize: number;
  minClipMs?: number;
  maxClipMs?: number;
  maxClipDriftToleranceMs?: number;
  maxClipBytes?: number;
}): boolean {
  const minClipMs = args.minClipMs ?? VOICE_RECORDING_MIN_CLIP_MS;
  const maxClipMs = args.maxClipMs ?? VOICE_RECORDING_MAX_CLIP_MS;
  const maxClipDriftToleranceMs =
    args.maxClipDriftToleranceMs ?? VOICE_RECORDING_MAX_CLIP_DRIFT_TOLERANCE_MS;
  const maxClipBytes = args.maxClipBytes ?? VOICE_RECORDING_MAX_CLIP_BYTES;
  return (
    args.byteSize > 0 &&
    args.byteSize <= maxClipBytes &&
    args.durationMs >= minClipMs &&
    args.durationMs <= maxClipMs + maxClipDriftToleranceMs
  );
}

export function getRealtimeVoiceRecordingFileExtension(
  mimeType: string | undefined
): string {
  const normalized = mimeType?.toLowerCase() ?? '';
  if (normalized.includes('mp4')) return 'm4a';
  if (normalized.includes('webm')) return 'webm';
  return 'webm';
}

export function getRealtimeVoiceRecordingFilename(args: {
  role: VoiceRecordingRole;
  sequence: number;
  mimeType: string | undefined;
}): string {
  const extension = getRealtimeVoiceRecordingFileExtension(args.mimeType);
  return `voice-${args.role}-${Math.max(0, Math.round(args.sequence))}.${extension}`;
}

export function clampRealtimeVoiceRecordingDuration(
  durationMs: number
): number {
  return Math.min(
    Math.max(0, Math.round(durationMs)),
    VOICE_RECORDING_MAX_CLIP_MS
  );
}
